<template>
    <div class="monthly-statistics-view">
        <v-main>
            <year-month-range-picker v-model="period" @valid="onValid" @reload="load">
                <template #actions>
                    <v-btn color="primary" :loading="loading" :disabled="!valid" @click="load">
                        {{ t('mpcStatistics.resources.update') }}
                    </v-btn>
                </template>
            </year-month-range-picker>

            <template v-if="store.byDayLineDeparture && store.byDayLineDeparture.length > 0">
                <v-row>
                    <v-col>
                        <div class="v-table v-theme--light">
                            <div class="v-table__wrapper">
                                <table aria-label="Monthly statistics" class="mpc-table">
                                    <thead>
                                        <tr>
                                            <td></td>
                                            <th class="headers-title" colspan="2" scope="colgroup">
                                                {{ t('mpcStatistics.resources.passengers') }}
                                            </th>
                                            <th class="headers-title" colspan="2" scope="colgroup">
                                                {{ t('mpcStatistics.resources.vehicle') }}
                                            </th>
                                            <td></td>
                                        </tr>
                                        <tr>
                                            <th class="headers-title" scope="col">{{ t('mpcStatistics.resources.monthAndLine') }}</th>
                                            <th class="headers-title" scope="col">{{ t('mpcStatistics.resources.in') }}</th>
                                            <th class="headers-title" scope="col">{{ t('mpcStatistics.resources.out') }}</th>
                                            <th class="headers-title" scope="col">{{ t('mpcStatistics.resources.in') }}</th>
                                            <th class="headers-title" scope="col">{{ t('mpcStatistics.resources.out') }}</th>
                                            <th class="headers-title" scope="col">
                                                {{ t('mpcStatistics.resources.carsLeftBehind') }}
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <template v-for="m in monthsSummary" :key="m.month">
                                            <tr class="month-summary">
                                                <td>{{ formatYearMonth(m.month) }}</td>
                                                <td>{{ m.passengersIn }}</td>
                                                <td>{{ m.passengersOut }}</td>
                                                <td>{{ m.carsIn }}</td>
                                                <td>{{ m.carsOut }}</td>
                                                <td>{{ m.carsLeftBehind }}</td>
                                            </tr>
                                            <tr v-for="line in m.lines" :key="`${m.month}-${line.lineNumber}`" class="line-row">
                                                <td>{{ line.lineName }}</td>
                                                <td>{{ line.passengersIn }}</td>
                                                <td>{{ line.passengersOut }}</td>
                                                <td>{{ line.carsIn }}</td>
                                                <td>{{ line.carsOut }}</td>
                                                <td>{{ line.carsLeftBehind }}</td>
                                            </tr>
                                        </template>
                                    </tbody>
                                    <tfoot>
                                        <tr>
                                            <td>
                                                <strong>{{ t('mpcStatistics.resources.sum') }}</strong>
                                            </td>
                                            <td>
                                                <strong>{{ totals.passengersIn }}</strong>
                                            </td>
                                            <td>
                                                <strong>{{ totals.passengersOut }}</strong>
                                            </td>
                                            <td>
                                                <strong>{{ totals.carsIn }}</strong>
                                            </td>
                                            <td>
                                                <strong>{{ totals.carsOut }}</strong>
                                            </td>
                                            <td>
                                                <strong>{{ totals.carsLeftBehind }}</strong>
                                            </td>
                                        </tr>
                                    </tfoot>
                                </table>
                            </div>
                        </div>
                    </v-col>
                </v-row>
            </template>
            <v-row v-else-if="loaded">
                <v-col class="text-center"
                    ><em>{{ t('mpcStatistics.resources.noDataForPeriod') }}</em></v-col
                >
            </v-row>
        </v-main>
    </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import YearMonthRangePicker from './components/YearMonthRangePicker.vue';
import { formatYearMonth } from './monthFormat';
import type { MonthSummary } from './mpcStatisticsModels';
import { useMpcStatisticsStore } from './mpcStatisticsStore';

const { t } = useI18n();
const store = useMpcStatisticsStore();
const period = ref({ from: store.monthlyPeriod.from, to: store.monthlyPeriod.to });
const valid = ref(true);
const loading = ref(false);
const loaded = ref(false);

const monthsSummary = computed<MonthSummary[]>(() => {
    const rows = store.byDayLineDeparture ?? [];
    const byMonth = new Map<string, MonthSummary>();
    for (const r of rows) {
        const month = r.day.slice(0, 7);
        let m = byMonth.get(month);
        if (!m) {
            m = {
                month,
                passengersIn: 0,
                passengersOut: 0,
                carsIn: 0,
                carsOut: 0,
                carsLeftBehind: 0,
                lines: []
            };
            byMonth.set(month, m);
        }
        m.passengersIn += r.passengersIn;
        m.passengersOut += r.passengersOut;
        m.carsIn += r.carsIn;
        m.carsOut += r.carsOut;
        m.carsLeftBehind += r.carsLeftBehind;

        let line = m.lines.find((l) => l.lineNumber === r.lineNumber);
        if (!line) {
            line = {
                lineNumber: r.lineNumber,
                lineName: r.lineName,
                passengersIn: 0,
                passengersOut: 0,
                carsIn: 0,
                carsOut: 0,
                carsLeftBehind: 0
            };
            m.lines.push(line);
        }
        line.passengersIn += r.passengersIn;
        line.passengersOut += r.passengersOut;
        line.carsIn += r.carsIn;
        line.carsOut += r.carsOut;
        line.carsLeftBehind += r.carsLeftBehind;
    }
    const months = [...byMonth.values()].sort((a, b) => a.month.localeCompare(b.month));
    for (const m of months) {
        m.lines.sort((a, b) => a.lineNumber.localeCompare(b.lineNumber, undefined, { numeric: true }));
    }
    return months;
});

const totals = computed(() => {
    return monthsSummary.value.reduce(
        (acc, m) => ({
            passengersIn: acc.passengersIn + m.passengersIn,
            passengersOut: acc.passengersOut + m.passengersOut,
            carsIn: acc.carsIn + m.carsIn,
            carsOut: acc.carsOut + m.carsOut,
            carsLeftBehind: acc.carsLeftBehind + m.carsLeftBehind
        }),
        { passengersIn: 0, passengersOut: 0, carsIn: 0, carsOut: 0, carsLeftBehind: 0 }
    );
});

function onValid(v: boolean) {
    valid.value = v;
}

async function load() {
    if (!valid.value) return;
    loading.value = true;
    try {
        await store.loadByDayLineDeparture(period.value.from, period.value.to);
        loaded.value = true;
    } finally {
        loading.value = false;
    }
}

onMounted(() => {
    load();
});
</script>

<style lang="scss" scoped>
.mpc-table {
    text-align: center;
    width: 100%;
    border-spacing: 0;
    margin-top: 12px;
}

.headers-title {
    text-align: center !important;
    border-bottom: none;
}

.v-table__wrapper {
    thead {
        tr:nth-child(2) th {
            box-shadow: inset 0 -2px 0 #000000;
        }

        tr th {
            font-size: 0.875rem;
            height: 48px;
            border: none !important;
        }
    }

    tbody tr td {
        font-size: 0.875rem;
        height: 48px;
    }

    tfoot tr td {
        box-shadow: inset 0 2px 0 #000000;
        font-size: 0.875rem;
        height: 48px;
    }
}

.month-summary td {
    font-weight: 700;
    box-shadow: inset 0 2px 0 rgba(0, 0, 0, 0.4);
    background-color: #eeeeee;
}

.month-summary td:first-child,
.line-row td:first-child {
    text-align: left;
}

.line-row td:first-child {
    padding-left: 24px;
}
</style>
